import { forwardRef } from 'react'
import { cn } from './cn'
import { Spinner } from './spinner'

export type ButtonVariant = 'primary' | 'accent' | 'ghost' | 'danger' | 'warning'
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: ButtonVariant
	size?: ButtonSize
	leadingIcon?: React.ReactNode
	trailingIcon?: React.ReactNode
	loading?: boolean
	block?: boolean
}

const base =
	'inline-flex items-center justify-center gap-2 font-medium transition-all duration-200 ease-out disabled:opacity-50 disabled:cursor-not-allowed select-none'

const sizeMap: Record<ButtonSize, string> = {
	xs: 'text-xs px-2.5 py-1 rounded-lg',
	sm: 'text-sm px-3 py-1.5 rounded-lg',
	md: 'text-sm px-6 py-3 rounded-xl shadow-soft hover:shadow-soft-lg active:scale-[0.98]',
	lg: 'text-base px-7 py-3.5 rounded-xl shadow-soft hover:shadow-soft-lg active:scale-[0.98]',
}

const variantMap: Record<ButtonVariant, string> = {
	primary: 'bg-primary text-primary-content hover:brightness-95',
	accent: 'bg-accent text-accent-content hover:brightness-95',
	ghost: 'text-base-content/80 hover:bg-base-200 hover:text-base-content active:bg-base-300',
	danger: 'bg-coral-600 text-white hover:brightness-95',
	warning: 'bg-amber-500 text-white hover:brightness-95',
}

/**
 * The single source of truth for button visuals. Use this to style a
 * non-`<button>` element (most often a react-router `<Link>`) so it matches a
 * real `<Button>` exactly — pass the result through `cn()` with any extra
 * utilities. For an actual `<button>`, render `<Button>` instead.
 */
export function buttonClasses({
	variant = 'primary',
	size = 'md',
	block = false,
}: {
	variant?: ButtonVariant
	size?: ButtonSize
	block?: boolean
} = {}): string {
	return cn(base, sizeMap[size], variantMap[variant], block && 'w-full')
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
	{
		variant = 'primary',
		size = 'md',
		leadingIcon,
		trailingIcon,
		loading,
		block,
		className,
		children,
		disabled,
		type = 'button',
		...rest
	},
	ref,
) {
	return (
		<button
			ref={ref}
			type={type}
			disabled={disabled || loading}
			aria-busy={loading || undefined}
			className={cn(buttonClasses({ variant, size, block }), className)}
			{...rest}
		>
			{loading ? <Spinner size={size === 'lg' ? 'md' : 'sm'} /> : leadingIcon}
			{children}
			{!loading && trailingIcon}
		</button>
	)
})
